// frontend/src/components/CookieBanner.jsx
import React, { useEffect, useState } from "react";
import { recordConsent, recordCookieConsent } from "../api/auth";
import { getAccessToken } from "../api/client";

/**
 * localStorage key holding the user's cookie choice, e.g.
 * ``{ "analytics": true, "essential": true }``. Essential cookies
 * (authentication) are always on; only analytics is optional.
 */
export const COOKIE_CONSENT_KEY = "cookie_consent";

/**
 * Window event that re-opens the cookie-preferences chooser as a modal. The
 * footer "Cookie Preferences" link dispatches it so a user can change a choice
 * they already made (the banner itself only shows on first visit).
 */
export const OPEN_COOKIE_PREFS_EVENT = "open-cookie-preferences";

/** Read + parse the stored consent, or ``null`` when no choice has been made. */
function readConsent() {
  try {
    return JSON.parse(localStorage.getItem(COOKIE_CONSENT_KEY));
  } catch {
    return null; // corrupt value → treat as "no choice yet"
  }
}

const BANNER_TEXT =
  "QRFlow uses essential cookies for authentication and optional analytics " +
  "cookies for improving your experience.";

/**
 * CookieBanner — first-visit cookie consent banner + re-openable preferences.
 *
 * Shows a bottom banner when no choice is stored. "Accept all" enables analytics
 * cookies; "Essential only" disables them. Either choice is persisted to
 * localStorage and the banner does not show again. The same chooser re-opens as
 * a modal when an {@link OPEN_COOKIE_PREFS_EVENT} fires (footer link), letting
 * the user revise their decision.
 *
 * Rendered at the app root, so it is available on every route (including the
 * public login/register pages).
 */
function CookieBanner() {
  const [consent, setConsent] = useState(() => readConsent());
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const open = () => setEditing(true);
    window.addEventListener(OPEN_COOKIE_PREFS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_PREFS_EVENT, open);
  }, []);

  const choose = (analytics) => {
    const value = { analytics, essential: true };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(value));
    setConsent(value);
    setEditing(false);
    // Record the acceptance server-side for the consent ledger (admin-visible).
    // Signed-in users record against their account; logged-out visitors record a
    // user-less row keyed to the anonymised IP. Best-effort — the browser choice
    // above always applies regardless of the network result.
    const persist = getAccessToken()
      ? recordConsent("cookie_policy")
      : recordCookieConsent();
    persist.catch(() => {});
  };

  // The banner shows only until a first choice is made; the modal shows on
  // demand (footer link) regardless of whether a choice already exists.
  const showBanner = consent === null;
  if (!showBanner && !editing) return null;

  const buttons = (
    <div className="flex flex-shrink-0 gap-3">
      <button
        type="button"
        onClick={() => choose(false)}
        className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        Essential only
      </button>
      <button
        type="button"
        onClick={() => choose(true)}
        className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700"
      >
        Accept all
      </button>
    </div>
  );

  // Preferences modal (re-opened from the footer) takes precedence.
  if (editing) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-prefs-title"
        onClick={() => setEditing(false)}
      >
        <div
          className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h2
            id="cookie-prefs-title"
            className="text-lg font-semibold text-gray-900 mb-2"
          >
            Cookie Preferences
          </h2>
          <p className="text-sm text-gray-600 mb-5">{BANNER_TEXT}</p>
          {buttons}
        </div>
      </div>
    );
  }

  // First-visit banner.
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 bg-white border-t border-gray-200 shadow-lg"
      role="region"
      aria-label="Cookie consent"
    >
      <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <p className="flex-1 text-sm text-gray-700">{BANNER_TEXT}</p>
        {buttons}
      </div>
    </div>
  );
}

export default CookieBanner;
